/**
 * Damage analytics dashboard.
 *
 * Cross-reservation aggregated stats from activity_log
 * (damage-analysis-completed entries). Surfaces:
 *   - total reports + damages with date-range filter
 *   - bar chart: damages per month
 *   - pie chart: severity distribution
 *   - bar charts: top vehicles, top clients, top damage types
 *
 * Route: /svc/damage-analytics (registered separately because it's not
 * scoped to one reservation).
 */

import { useState } from 'react'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import { useDamageStats } from '../api/damage-analysis'
import { LOADING } from '../components/loading'
import { Paper } from '../components/paper'

const SEVERITY_COLORS = ['#eab308', '#f97316', '#ef4444'] // 1, 2, 3
const SEVERITY_LABELS = ['drobné', 'znatelné', 'vážné']

const TYPE_LABELS_CS: Record<string, string> = {
	scratch: 'Škrábanec',
	dent: 'Důlek',
	scuff: 'Oder',
	crack: 'Prasklina',
	chip: 'Odštípnutí',
	missing_part: 'Chybějící díl',
	dirt_stain: 'Skvrna',
	other: 'Jiné',
}

function isoDateNDaysAgo(n: number) {
	const d = new Date()
	d.setUTCDate(d.getUTCDate() - n)
	return d.toISOString().slice(0, 10)
}

export function DamageAnalyticsRoute() {
	const [from, setFrom] = useState<string>(isoDateNDaysAgo(90))
	const [to, setTo] = useState<string>(new Date().toISOString().slice(0, 10))
	const stats = useDamageStats({ from: from || undefined, to: to || undefined })

	if (stats.isLoading) {
		return (
			<div className="container m-auto p-4">
				<Paper className="p-8">
					<div className="flex justify-center">{LOADING}</div>
				</Paper>
			</div>
		)
	}

	if (stats.isError || !stats.data) {
		return (
			<div className="container m-auto p-4">
				<Paper className="p-6 border-red-200 bg-red-50">
					<p className="text-sm text-red-800">Statistiky se nepodařilo načíst.</p>
				</Paper>
			</div>
		)
	}

	const d = stats.data

	const monthData = Object.entries(d.dimensions.by_month)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([month, count]) => ({ month, count }))

	const severityData = [1, 2, 3].map(s => ({
		name: SEVERITY_LABELS[s - 1],
		value: d.dimensions.by_severity[s as 1 | 2 | 3] || 0,
		color: SEVERITY_COLORS[s - 1],
	}))

	const typeData = Object.entries(d.dimensions.by_type)
		.sort(([, a], [, b]) => b - a)
		.slice(0, 10)
		.map(([type, count]) => ({ type: TYPE_LABELS_CS[type] || type, count }))

	const topVehicles = Object.entries(d.dimensions.by_vehicle)
		.sort(([, a], [, b]) => b - a)
		.slice(0, 10)
	const topClients = Object.entries(d.dimensions.by_client)
		.sort(([, a], [, b]) => b - a)
		.slice(0, 10)

	return (
		<div className="container m-auto p-4 space-y-4">
			<div className="flex items-end justify-between gap-4 flex-wrap">
				<div>
					<h2 className="text-lg font-bold">Analýza poškození vozidel</h2>
					<p className="text-xs text-slate-500 mt-1">
						Souhrnný pohled na všechna AI vyhodnocení v daném období.
					</p>
				</div>
				<div className="flex items-end gap-2">
					<label className="text-xs text-slate-600">
						Od
						<input
							type="date"
							value={from}
							onChange={e => setFrom(e.target.value)}
							className="block border border-slate-300 rounded px-2 py-1 text-sm"
						/>
					</label>
					<label className="text-xs text-slate-600">
						Do
						<input
							type="date"
							value={to}
							onChange={e => setTo(e.target.value)}
							className="block border border-slate-300 rounded px-2 py-1 text-sm"
						/>
					</label>
				</div>
			</div>

			<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
				<Paper className="p-4 text-center">
					<div className="text-xs uppercase tracking-wide text-slate-400">
						Reportů celkem
					</div>
					<div className="text-2xl font-bold mt-1">{d.total_reports}</div>
				</Paper>
				<Paper className="p-4 text-center">
					<div className="text-xs uppercase tracking-wide text-slate-400">
						Poškození nahlášených
					</div>
					<div className="text-2xl font-bold mt-1">{d.total_damages}</div>
				</Paper>
				<Paper className="p-4 text-center">
					<div className="text-xs uppercase tracking-wide text-slate-400">
						Průměr na rezervaci
					</div>
					<div className="text-2xl font-bold mt-1">
						{d.total_reports > 0 ? (d.total_damages / d.total_reports).toFixed(1) : '0'}
					</div>
				</Paper>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<Paper className="p-4">
					<h3 className="text-sm font-bold text-slate-700 mb-2">Vývoj v čase</h3>
					{monthData.length === 0 ? (
						<EmptyChart />
					) : (
						<ResponsiveContainer width="100%" height={240}>
							<BarChart data={monthData}>
								<CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
								<XAxis dataKey="month" fontSize={11} />
								<YAxis allowDecimals={false} fontSize={11} />
								<Tooltip />
								<Bar dataKey="count" fill="#3b82f6" />
							</BarChart>
						</ResponsiveContainer>
					)}
				</Paper>

				<Paper className="p-4">
					<h3 className="text-sm font-bold text-slate-700 mb-2">Rozložení závažnosti</h3>
					{severityData.every(s => s.value === 0) ? (
						<EmptyChart />
					) : (
						<ResponsiveContainer width="100%" height={240}>
							<PieChart>
								<Pie
									data={severityData}
									dataKey="value"
									nameKey="name"
									label
									outerRadius={80}
								>
									{severityData.map((entry, i) => (
										<Cell key={i} fill={entry.color} />
									))}
								</Pie>
								<Legend />
								<Tooltip />
							</PieChart>
						</ResponsiveContainer>
					)}
				</Paper>
			</div>

			<Paper className="p-4">
				<h3 className="text-sm font-bold text-slate-700 mb-2">
					Nejčastější typy poškození
				</h3>
				{typeData.length === 0 ? (
					<EmptyChart />
				) : (
					<ResponsiveContainer width="100%" height={260}>
						<BarChart data={typeData} layout="vertical">
							<CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
							<XAxis type="number" allowDecimals={false} fontSize={11} />
							<YAxis dataKey="type" type="category" width={110} fontSize={11} />
							<Tooltip />
							<Bar dataKey="count" fill="#8b5cf6" />
						</BarChart>
					</ResponsiveContainer>
				)}
			</Paper>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<Paper className="p-4">
					<h3 className="text-sm font-bold text-slate-700 mb-2">
						TOP problémová vozidla
					</h3>
					{topVehicles.length === 0 ? (
						<EmptyChart small />
					) : (
						<ul className="text-sm space-y-1">
							{topVehicles.map(([id, count]) => (
								<li
									key={id}
									className="flex justify-between border-b border-slate-100 pb-1"
								>
									<a
										className="text-blue-600 hover:underline truncate"
										href={`/svc/reservation/vehicle/${id}`}
									>
										{id}
									</a>
									<span className="font-bold tabular-nums">{count}</span>
								</li>
							))}
						</ul>
					)}
				</Paper>
				<Paper className="p-4">
					<h3 className="text-sm font-bold text-slate-700 mb-2">
						TOP problémoví klienti
					</h3>
					{topClients.length === 0 ? (
						<EmptyChart small />
					) : (
						<ul className="text-sm space-y-1">
							{topClients.map(([id, count]) => (
								<li
									key={id}
									className="flex justify-between border-b border-slate-100 pb-1"
								>
									<a
										className="text-blue-600 hover:underline truncate"
										href={`/svc/reservation/client/${id}`}
									>
										{id}
									</a>
									<span className="font-bold tabular-nums">{count}</span>
								</li>
							))}
						</ul>
					)}
				</Paper>
			</div>
		</div>
	)
}

function EmptyChart({ small = false }: { small?: boolean }) {
	return (
		<div
			className={`flex items-center justify-center text-sm text-slate-400 ${
				small ? 'h-20' : 'h-44'
			}`}
		>
			Žádná data v tomto období
		</div>
	)
}
